import type { DatumId } from "@nivo/pie";
import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/shared/uikit/Typography";

interface TooltipDataItem {
  color: string;
  formattedPercentage: string;
  id: DatumId;
  label: string;
  value: number;
}

interface ChartTooltipProps {
  data: TooltipDataItem[];
  dateValue?: string;
  prefix?: string;
  rootLocale?: string[];
  selected: DatumId;
  showDate?: boolean;
  showPercentages?: boolean;
  showTotal?: boolean;
  suffix?: string;
}

export const ChartTooltip = ({
  data,
  selected,
  prefix,
  suffix,
  rootLocale,
  showDate,
  showTotal = true,
  showPercentages = true,
  dateValue,
}: ChartTooltipProps) => {
  const total = data.reduce((acc, item) => acc + item.value, 0);
  const selectedItem = data.find(item => item.id === selected);
  const otherItems = data.filter(item => item.id !== selected);

  if (!selectedItem) {
    return null;
  }

  const areOtherItems = otherItems.length > 0;
  const shouldShowPercentages = showPercentages && areOtherItems;
  const shouldShowTotal = showTotal && areOtherItems;

  return (
    <div className="bg-primary w-max rounded-lg p-4 shadow-md">
      {showDate && dateValue && (
        <div className="border-secondary mb-3 border-b pb-2">
          <Typography.LabelSm weight="semibold">{dateValue}</Typography.LabelSm>
        </div>
      )}
      <table className="table-auto">
        <tbody>
          {}
          <tr className={areOtherItems ? "border-secondary border-b" : ""}>
            <td className="whitespace-nowrap py-2 pr-6">
              <div className="flex items-center gap-2">
                <div
                  className="size-3 rounded-full"
                  style={{ backgroundColor: selectedItem.color }}
                />
                <Typography.LabelSm weight="semibold">
                  {rootLocale
                    ? t([...rootLocale, selectedItem.label])
                    : selectedItem.label}
                </Typography.LabelSm>
              </div>
            </td>
            <td className="whitespace-nowrap py-2 pr-6 text-center">
              <Typography.LabelSm weight="semibold">
                {prefix}
                {selectedItem.value.toLocaleString()}
                {suffix}
              </Typography.LabelSm>
            </td>
            {shouldShowPercentages && (
              <td className="whitespace-nowrap py-2 text-center">
                <Typography.LabelSm weight="semibold">
                  {selectedItem.formattedPercentage}
                </Typography.LabelSm>
              </td>
            )}
          </tr>
          {otherItems.map(item => (
            <tr
              key={item.id}
              className="border-secondary border-b last:border-b-0"
            >
              <td className="whitespace-nowrap py-2 pr-6">
                <div className="flex items-center gap-2">
                  <div
                    className="size-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <Typography.LabelSm>
                    {rootLocale ? t([...rootLocale, item.label]) : item.label}
                  </Typography.LabelSm>
                </div>
              </td>
              <td className="whitespace-nowrap py-2 pr-6 text-center">
                <Typography.LabelSm>
                  {prefix}
                  {item.value.toLocaleString()}
                  {suffix}
                </Typography.LabelSm>
              </td>
              {shouldShowPercentages && (
                <td className="whitespace-nowrap py-2 text-center">
                  <Typography.LabelSm>
                    {item.formattedPercentage}
                  </Typography.LabelSm>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        {shouldShowTotal && (
          <tfoot>
            <tr className="border-secondary border-t">
              <td className="whitespace-nowrap pr-6 pt-2 font-medium">
                <Typography.LabelSm>
                  {t("settings.analytics.total")}
                </Typography.LabelSm>
              </td>
              <td className="whitespace-nowrap pr-6 pt-2 text-center font-medium">
                <Typography.LabelSm>
                  {prefix}
                  {total.toLocaleString()}
                  {suffix}
                </Typography.LabelSm>
              </td>
              {shouldShowPercentages && (
                <td className="pt-2 text-center font-medium" />
              )}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};
